import Styles from './PostApproval.module.scss'
import { Button } from 'blaze-ui/Button'
import { useCurrentWorkspace } from 'hooks/useCurrentWorkspace'
import { AlmanacFileShowItem } from 'hooks/queries/files/types'
import { useUpdateFile } from 'hooks/queries/files/useUpdateFile'
import { useQueryClient } from 'react-query'
import { filesKeys } from 'hooks/queries/files/filesKeys'
import { PostStatusPropertyValue } from 'api/clients/PropertyClient'
import { showToastMessage } from 'common/ToastMessage/ShowToastMessage'
import dayjs from 'dayjs'
import { useHover } from '@react-aria/interactions'
import { useIsSystemRedesign } from 'hooks/useIsSystemRedesign'

interface ApprovedButtonProps {
  almanacFile: AlmanacFileShowItem
  isLoading: boolean
  setIsLoading: (isLoading: boolean) => void
  onUpdateAlmanacFile?: (updatedFile: AlmanacFileShowItem) => void
}

export const ApprovedButton = ({ almanacFile, isLoading, setIsLoading, onUpdateAlmanacFile }: ApprovedButtonProps) => {
  const isSystemRedesignEnabled = useIsSystemRedesign()
  const queryClient = useQueryClient()
  const workspace = useCurrentWorkspace()
  const { hoverProps, isHovered } = useHover({})
  const hoveredText = 'Move Back to Draft'
  const defaultText = `Approved on ${dayjs(almanacFile.reviewed_at).format('MMM D')}`
  const buttonText = isHovered ? hoveredText : defaultText

  const updateFileMutation = useUpdateFile(almanacFile.key, {
    onSuccess: (updatedFile) => {
      if (onUpdateAlmanacFile) {
        onUpdateAlmanacFile(updatedFile as AlmanacFileShowItem)
      }
      queryClient.invalidateQueries(filesKeys.partialCalendar(workspace?.id))
    },
  })

  const handleMoveBackToDraft = async () => {
    setIsLoading(true)

    try {
      await updateFileMutation.mutateAsync({
        post_status: PostStatusPropertyValue.Draft,
        reviewed_at: null,
      })
    } catch (error) {
      showToastMessage(`Failed to move back to draft`, 'error')
    } finally {
      setIsLoading(false)
    }
  }

  if (!almanacFile.reviewed_at) return null

  return (
    <div className={Styles.buttons}>
      <div {...hoverProps} className={Styles.buttonContainer}>
        <Button
          variant={isSystemRedesignEnabled ? 'secondary' : 'tertiary'}
          fullWidth
          onPress={handleMoveBackToDraft}
          isDisabled={isLoading}
        >
          {buttonText}
        </Button>
      </div>
    </div>
  )
}
